import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Search,
  X,
  User,
  FileText,
  CreditCard,
  BedDouble,
  Package,
  Stethoscope,
  Calendar,
  ChevronDown,
  ArrowRight,
  Sparkles,
  Maximize2,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import globalSearchService, {
  GlobalSearchResultItem,
  GlobalSearchCategory,
} from "@/services/globalSearchService";

interface GlobalOmnisearchProps {
  isDark?: boolean;
  className?: string;
  onOpenPatientModal?: () => void;
  onOpenAdmissionModal?: () => void;
}

const MODULE_SCOPES = [
  { key: "all", label: "All Modules", icon: Sparkles },
  { key: "patients", label: "Patients", icon: User },
  { key: "prescriptions", label: "Prescriptions", icon: FileText },
  { key: "invoices", label: "Invoices & Billing", icon: CreditCard },
  { key: "admissions", label: "Inpatient Wards", icon: BedDouble },
  { key: "items", label: "Pharmacy & Items", icon: Package },
  { key: "doctors", label: "Doctors & Staff", icon: Stethoscope },
  { key: "appointments", label: "Appointments", icon: Calendar },
];

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  patients: User,
  prescriptions: FileText,
  invoices: CreditCard,
  admissions: BedDouble,
  items: Package,
  doctors: Stethoscope,
  appointments: Calendar,
};

export const GlobalOmnisearch: React.FC<GlobalOmnisearchProps> = ({
  className,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedScope, setSelectedScope] = useState("all");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<GlobalSearchCategory[]>([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const modalInputRef = useRef<HTMLInputElement>(null);
  const scopeDropdownRef = useRef<HTMLDivElement>(null);

  // Flattened list of all result items for keyboard arrow navigation
  const flatItems = useMemo(() => {
    return categories.flatMap((cat) => cat.items);
  }, [categories]);

  // Global hotkey listener for "/" and "Cmd+K" / "Ctrl+K"
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeTag = document.activeElement?.tagName.toLowerCase();
      const isInputActive = activeTag === "input" || activeTag === "textarea" || activeTag === "select";

      if (e.key === "/" && !isInputActive) {
        e.preventDefault();
        headerInputRef.current?.focus();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsModalOpen(true);
      } else if (e.key === "Escape") {
        if (isModalOpen) {
          setIsModalOpen(false);
        } else if (isDropdownOpen) {
          setIsDropdownOpen(false);
          headerInputRef.current?.blur();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDropdownOpen, isModalOpen]);

  // Focus modal input on open
  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => {
        modalInputRef.current?.focus();
        modalInputRef.current?.select();
      }, 50);
    }
  }, [isModalOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
        setScopeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Perform debounced live search
  useEffect(() => {
    if (query.trim().length < 2) {
      setCategories([]);
      setTotalMatches(0);
      setLoading(false);
      setIsDropdownOpen(false);
      return;
    }

    setLoading(true);
    setIsDropdownOpen(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await globalSearchService.search(query, selectedScope, 6, controller.signal);
        if (controller.signal.aborted) return;
        setCategories(res.categories || []);
        setTotalMatches(res.totalMatches || 0);
        setActiveIndex(0);
      } catch {
        // Cancelled searches are obsolete; the next query owns the state.
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 350);

    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, selectedScope]);

  // Handle keyboard navigation inside search list
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (flatItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const selected = flatItems[activeIndex];
      if (selected) {
        handleSelectItem(selected);
      }
    }
  };

  const handleSelectItem = (item: GlobalSearchResultItem) => {
    setIsDropdownOpen(false);
    setIsModalOpen(false);
    if (item.route) {
      navigate(item.route);
    }
  };

  // Substring highlight helper
  const highlightMatch = (text: string, highlight: string) => {
    if (!highlight.trim() || !text) return text;
    const regex = new RegExp(`(${highlight.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-primary/20 text-primary font-bold rounded-xs px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const currentScopeObj = MODULE_SCOPES.find((s) => s.key === selectedScope) || MODULE_SCOPES[0];

  // Pure Search Results Renderer (no empty boxes)
  const renderResultsList = () => (
    <>
      {loading && (
        <div className="flex items-center justify-center py-6 text-muted-foreground gap-2 text-xs">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span>Searching records...</span>
        </div>
      )}

      {!loading && query.trim() && categories.length === 0 && (
        <div className="text-center py-6 text-xs text-muted-foreground">
          No matches found for <strong className="text-foreground">&ldquo;{query}&rdquo;</strong>
        </div>
      )}

      {!loading &&
        categories.map((category) => {
          const CategoryIcon = CATEGORY_ICONS[category.key] || Sparkles;
          return (
            <div key={category.key} className="space-y-1">
              <div className="flex items-center justify-between px-2 pt-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <CategoryIcon className="w-3 h-3 text-primary" />
                  <span>{category.label}</span>
                </span>
                <span className="text-[10px] font-mono text-muted-foreground">
                  {category.count}
                </span>
              </div>

              <div className="divide-y divide-border/40">
                {category.items.map((item) => {
                  const itemIndex = flatItems.findIndex((f) => f.id === item.id);
                  const isHighlighted = itemIndex === activeIndex;

                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSelectItem(item)}
                      onMouseEnter={() => setActiveIndex(itemIndex)}
                      className={cn(
                        "px-3 py-2 rounded-lg flex items-center justify-between gap-3 transition-colors cursor-pointer",
                        isHighlighted
                          ? "bg-primary/10 text-foreground"
                          : "hover:bg-muted/60 text-foreground"
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-foreground">
                            {highlightMatch(item.title, query)}
                          </span>
                          {item.badge && (
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[9px] font-semibold px-1.5 py-0",
                                item.badgeVariant === "success" &&
                                  "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
                                item.badgeVariant === "warning" &&
                                  "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                              )}
                            >
                              {item.badge}
                            </Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground font-mono truncate">
                          {highlightMatch(item.subtitle, query)}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 text-[11px] text-primary font-medium flex-shrink-0">
                        <span className="hidden sm:inline">Open</span>
                        <ArrowRight className="w-3 h-3" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </>
  );

  return (
    <div ref={containerRef} className={cn("relative w-full max-w-lg", className)}>
      {/* ─────────────────────────────────────────────────────────────
          1. HEADER DIRECT TYPEABLE SEARCH BAR
         ───────────────────────────────────────────────────────────── */}
      <div
        className={cn(
          "flex items-center w-full h-8.5 rounded-lg border text-xs transition-all",
          "bg-muted/40 border-border/70 text-foreground shadow-2xs",
          isDropdownOpen && query.trim() && "border-primary ring-1 ring-primary/30 bg-background"
        )}
      >
        {/* Scope selector dropdown */}
        <div className="relative" ref={scopeDropdownRef}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setScopeDropdownOpen(!scopeDropdownOpen);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 border-r border-border/60 text-[11px] font-medium text-foreground hover:bg-muted/60 transition-colors cursor-pointer"
            title="Change search scope"
          >
            <currentScopeObj.icon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
            <span className="hidden sm:inline max-w-[90px] truncate">{currentScopeObj.label}</span>
            <ChevronDown className="w-3 h-3 text-muted-foreground ml-0.5" />
          </button>

          {scopeDropdownOpen && (
            <div className="absolute left-0 top-full mt-1.5 w-52 rounded-xl border border-border/80 bg-popover shadow-xl z-50 p-1 animate-in fade-in-50 zoom-in-95 duration-100">
              <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border/50 mb-1">
                Search Scope
              </div>
              {MODULE_SCOPES.map((scope) => {
                const Icon = scope.icon;
                const isSelected = scope.key === selectedScope;
                return (
                  <button
                    key={scope.key}
                    type="button"
                    onClick={() => {
                      setSelectedScope(scope.key);
                      setScopeDropdownOpen(false);
                      headerInputRef.current?.focus();
                    }}
                    className={cn(
                      "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-colors cursor-pointer text-left",
                      isSelected
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "text-foreground hover:bg-muted/70"
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{scope.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Live Search Input directly in Header */}
        <div className="flex-1 flex items-center gap-1.5 px-2">
          <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          <input
            ref={headerInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              if (query.trim()) setIsDropdownOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={t("header.searchPlaceholder", "Search patients, Rx, invoices, items...")}
            className="w-full bg-transparent border-0 outline-none text-xs text-foreground placeholder:text-muted-foreground"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setIsDropdownOpen(false);
                headerInputRef.current?.focus();
              }}
              className="p-1 text-muted-foreground hover:text-foreground rounded-full"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Right controls: Fullscreen Expand icon + shortcut badge */}
        <div className="flex items-center gap-1 pr-1.5">
          <button
            type="button"
            onClick={() => {
              setIsDropdownOpen(false);
              setIsModalOpen(true);
            }}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
            title="Expand to Command Palette (⌘K)"
          >
            <Maximize2 className="w-3 h-3" />
          </button>
          <kbd className="hidden sm:inline-block px-1 py-0.5 rounded bg-background/80 border border-border/80 text-[9px] font-mono text-muted-foreground shadow-2xs">
            /
          </kbd>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          2. DIRECT SEARCH RESULTS DROPDOWN (Shown after typing query)
         ───────────────────────────────────────────────────────────── */}
      {isDropdownOpen && query.trim().length > 0 && (
        <div className="absolute left-0 top-full mt-1.5 w-[560px] md:w-[620px] max-h-[420px] rounded-xl bg-popover/95 backdrop-blur-md border border-border shadow-2xl z-50 flex flex-col overflow-hidden animate-in fade-in-50 zoom-in-98 duration-100">
          {/* Results List */}
          <div className="flex-1 overflow-y-auto p-2.5 space-y-3">
            {renderResultsList()}
          </div>

          {/* Minimal footer */}
          <div className="px-3 py-2 border-t border-border/60 bg-muted/30 flex items-center justify-between text-[10px] text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>
                <kbd className="px-1 py-0.5 rounded bg-background border border-border text-[9px] font-mono">
                  ↑
                </kbd>{" "}
                <kbd className="px-1 py-0.5 rounded bg-background border border-border text-[9px] font-mono">
                  ↓
                </kbd>{" "}
                navigate
              </span>
              <span>
                <kbd className="px-1 py-0.5 rounded bg-background border border-border text-[9px] font-mono">
                  ↵
                </kbd>{" "}
                open
              </span>
              <span>
                <kbd className="px-1 py-0.5 rounded bg-background border border-border text-[9px] font-mono">
                  esc
                </kbd>{" "}
                dismiss
              </span>
            </div>

            {totalMatches > 0 && (
              <span className="font-mono font-semibold text-foreground">
                {totalMatches} results
              </span>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          3. OPTIONAL FULLSCREEN MODAL / COMMAND PALETTE
         ───────────────────────────────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-20 px-3 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl rounded-2xl bg-card border border-border shadow-2xl overflow-hidden flex flex-col max-h-[80vh] animate-in zoom-in-95 duration-150"
          >
            {/* Modal Input */}
            <div className="p-3.5 border-b border-border/70 bg-gradient-to-r from-primary/5 via-card to-background flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  ref={modalInputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t(
                    "header.modalSearchPlaceholder",
                    "Type to search patients, Rx numbers, invoices, medicines, doctors..."
                  )}
                  className="w-full h-10 pl-9 pr-8 text-sm bg-background/80 border border-border/80 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      modalInputRef.current?.focus();
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded-full"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="h-8 w-8 rounded-lg border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors cursor-pointer"
                title="Close (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scope Filter Pills in Modal */}
            <div className="px-4 py-2 border-b border-border/50 bg-muted/20 flex items-center gap-1.5 overflow-x-auto text-xs no-scrollbar">
              <span className="text-[10px] font-bold uppercase text-muted-foreground mr-1 flex-shrink-0">
                Scope:
              </span>
              {MODULE_SCOPES.map((scope) => {
                const isSelected = scope.key === selectedScope;
                return (
                  <button
                    key={scope.key}
                    type="button"
                    onClick={() => setSelectedScope(scope.key)}
                    className={cn(
                      "px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-all flex-shrink-0 cursor-pointer",
                      isSelected
                        ? "bg-primary text-primary-foreground shadow-2xs font-bold"
                        : "bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    {scope.label}
                  </button>
                );
              })}
            </div>

            {/* Modal Results */}
            <div className="flex-1 overflow-y-auto p-3.5 space-y-3">
              {renderResultsList()}
            </div>

            {/* Modal Footer */}
            <div className="p-3 border-t border-border/70 bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-3 text-[11px]">
                <span>
                  <kbd className="px-1 py-0.5 rounded bg-background border border-border text-[10px] font-mono">
                    ↑
                  </kbd>{" "}
                  <kbd className="px-1 py-0.5 rounded bg-background border border-border text-[10px] font-mono">
                    ↓
                  </kbd>{" "}
                  to navigate
                </span>
                <span>
                  <kbd className="px-1 py-0.5 rounded bg-background border border-border text-[10px] font-mono">
                    ↵
                  </kbd>{" "}
                  to open
                </span>
                <span>
                  <kbd className="px-1 py-0.5 rounded bg-background border border-border text-[10px] font-mono">
                    esc
                  </kbd>{" "}
                  to dismiss
                </span>
              </div>

              {totalMatches > 0 && (
                <span className="text-[11px] font-mono font-semibold text-foreground">
                  {totalMatches} results
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalOmnisearch;
